/*
 * scc-guardian-chamber-fx.js — procedural plasma + fire for pedestal renders (canvas, V4).
 *
 * This layer only ever draws for the GUARDIAN scene, and only for renders that do NOT already have
 * their energy baked into the artwork. The hall render and the newer pedestal renders ship with
 * their own plasma, base rings and brazier fire, so drawing over them double-exposes the effect and
 * looks wrong. The section marks those with data-baked="1" and this engine skips them entirely —
 * skipping the draw rather than just hiding the canvas, so we are not burning a rAF loop painting
 * something nobody can see.
 *
 * Screen-blend canvas. Reduced-motion safe. Mobile-throttled.
 */
(function () {
  "use strict";
  function reduced() { try { return matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) { return false; } }

  var GUARD = {
    cols: [50], top: 0.00, bot: 0.555, half: 0.18, emerge: 0.84,
    ringY: 0.895, ringRX: 0.205, ringRY: 0.030,
    fires: [ { x: 15.2, y: 80.4, s: 1.35 }, { x: 84.8, y: 80.4, s: 1.35 } ]
  };

  var PERSON = {
    "status-quo":            { seed: 1, flow: 1.00, jitter: 0.00 },
    "plausible-deniability": { seed: 2, flow: 1.28, jitter: 0.14 },
    "redacted":              { seed: 3, flow: 0.92, jitter: 0.30, conceal: true },
    "il-maestro":            { seed: 4, flow: 1.06, jitter: 0.00, precise: true }
  };
  function person(h) { return PERSON[h] || PERSON["status-quo"]; }

  function rnd(a, b) { return a + Math.random() * (b - a); }
  function fnoise(t, s) { return (Math.sin(t * 1.7 + s) + Math.sin(t * 0.9 + s * 2.3) + Math.sin(t * 2.7 + s * 0.7)) / 3; }

  function Engine(root, canvas) {
    var ctx = canvas.getContext("2d"), W = 0, H = 0, dpr = 1, raf = 0, run = false, t = 0, fc = 0;
    var parts = [], curE = 1.0;

    function resize() {
      /* The canvas is inset:0 inside the stage, so its own layout box is the stage box. Measuring
         the section here would use the full-bleed page width and stretch every effect. */
      var r = canvas.getBoundingClientRect();
      if (!r.width || !r.height) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = r.width; H = r.height;
      canvas.width = Math.max(1, W * dpr | 0); canvas.height = Math.max(1, H * dpr | 0);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function plasmaColumn(cxp, cfg, P, energy) {
      var cx = cxp / 100 * W, top = cfg.top * H, bot = cfg.bot * H, h = bot - top, w = cfg.half * W * 2;
      var s = P.seed;
      var breath = 0.66 + 0.34 * (0.5 + 0.5 * Math.sin(t * (0.45 + 0.09 * s) * P.flow + s * 1.7));
      var conceal = P.conceal ? (0.78 + 0.22 * (0.5 + 0.5 * Math.sin(t * 0.7 + s) * Math.sin(t * 0.23 + s * 2.1))) : 1.0;
      var lvl = energy * breath * conceal;

      ctx.save();
      ctx.beginPath(); ctx.rect(cx - w / 2, top, w, h); ctx.clip();

      var body = ctx.createLinearGradient(cx - w / 2, 0, cx + w / 2, 0);
      body.addColorStop(0.00, "rgba(20,70,180,0)");
      body.addColorStop(0.12, "rgba(12,75,225," + (0.12 * lvl) + ")");
      body.addColorStop(0.34, "rgba(18,105,250," + (0.18 * lvl) + ")");
      body.addColorStop(0.50, "rgba(45,155,255," + (0.26 * lvl) + ")");
      body.addColorStop(0.66, "rgba(18,105,250," + (0.18 * lvl) + ")");
      body.addColorStop(0.88, "rgba(12,75,225," + (0.12 * lvl) + ")");
      body.addColorStop(1.00, "rgba(20,70,180,0)");
      var fade = ctx.createLinearGradient(cx, top, cx, bot);
      fade.addColorStop(0, "rgba(255,255,255,.82)");
      fade.addColorStop(0.04, "rgba(255,255,255,1)");
      fade.addColorStop(0.94, "rgba(255,255,255,1)");
      fade.addColorStop(1, "rgba(255,255,255,0)");
      ctx.save();
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = body; ctx.fillRect(cx - w / 2, top, w, h);
      ctx.globalCompositeOperation = "destination-in";
      ctx.fillStyle = fade; ctx.fillRect(cx - w / 2, top, w, h);
      ctx.restore();

      var core = ctx.createLinearGradient(cx - w * 0.26, 0, cx + w * 0.26, 0);
      core.addColorStop(0, "rgba(70,145,255,0)");
      core.addColorStop(0.34, "rgba(80,165,255," + (0.06 * lvl) + ")");
      core.addColorStop(0.50, "rgba(165,225,255," + (0.14 * lvl) + ")");
      core.addColorStop(0.66, "rgba(80,165,255," + (0.06 * lvl) + ")");
      core.addColorStop(1, "rgba(70,145,255,0)");
      ctx.fillStyle = core; ctx.fillRect(cx - w * 0.26, top, w * 0.52, h);

      var broad = w > W * 0.25;
      var lanes = W < 700 ? (broad ? 16 : 11) : (broad ? 28 : 18);
      var rows = W < 700 ? 36 : 52, nodes = [], lane, row;
      for (lane = 0; lane < lanes; lane++) {
        nodes[lane] = [];
        for (row = 0; row <= rows; row++) {
          var u = row / rows, np = s * 3.1 + lane * 1.91 + row * 0.73;
          var baseX = cx - w * 0.43 + (lane / (lanes - 1)) * w * 0.86;
          var dx = Math.sin(t * (0.34 + lane * 0.016) * P.flow + np) * w * 0.018;
          dx += Math.sin(t * 0.17 - row * 0.83 + lane * 1.4) * w * 0.012;
          dx += Math.sin(row * 1.79 + lane * 2.27 + s * 0.61) * w * 0.016;
          var dy = Math.sin(t * 0.24 + np * 1.3) * h * 0.005;
          nodes[lane][row] = { x: baseX + dx, y: top + u * h + dy };
        }
      }

      ctx.lineCap = "round"; ctx.lineJoin = "round";
      ctx.shadowColor = "rgba(45,135,255,.78)"; ctx.shadowBlur = Math.max(1.5, w * 0.018);
      ctx.strokeStyle = "rgba(32,112,255," + (0.30 * lvl) + ")";
      ctx.lineWidth = Math.max(0.48, W * 0.00052);
      for (lane = 0; lane < lanes; lane++) {
        ctx.beginPath(); ctx.moveTo(nodes[lane][0].x, nodes[lane][0].y);
        for (row = 1; row <= rows; row++) ctx.lineTo(nodes[lane][row].x, nodes[lane][row].y);
        ctx.stroke();
      }

      ctx.strokeStyle = "rgba(65,166,255," + (0.34 * lvl) + ")";
      ctx.lineWidth = Math.max(0.42, W * 0.00046);
      for (lane = 0; lane < lanes - 1; lane++) {
        for (row = 1; row < rows; row++) {
          if (((lane * 7 + row * 5 + s * 3) % 7) > 4) continue;
          var from = nodes[lane][row];
          var toRow = Math.max(0, Math.min(rows, row + (((lane + row + s) % 3) - 1)));
          var to = nodes[lane + 1][toRow];
          ctx.beginPath(); ctx.moveTo(from.x, from.y);
          ctx.lineTo((from.x + to.x) * 0.5 + Math.sin(t * 0.42 + row + lane) * w * 0.025, (from.y + to.y) * 0.5);
          ctx.lineTo(to.x, to.y); ctx.stroke();
        }
      }

      ctx.strokeStyle = "rgba(120,205,255," + (0.42 * lvl) + ")";
      ctx.lineWidth = Math.max(0.75, W * 0.0009);
      for (lane = 2; lane < lanes; lane += 6) {
        ctx.beginPath(); ctx.moveTo(nodes[lane][0].x, nodes[lane][0].y);
        for (row = 1; row <= rows; row++) ctx.lineTo(nodes[lane][row].x, nodes[lane][row].y);
        ctx.stroke();
      }

      ctx.shadowColor = "rgba(80,175,255,.66)"; ctx.shadowBlur = Math.max(2, w * 0.018);
      ctx.strokeStyle = "rgba(120,205,255," + (0.26 * lvl) + ")";
      ctx.lineWidth = Math.max(0.6, W * 0.00065);
      ctx.beginPath(); ctx.moveTo(cx - w * 0.47, top); ctx.lineTo(cx - w * 0.47, bot); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx + w * 0.47, top); ctx.lineTo(cx + w * 0.47, bot); ctx.stroke();
      ctx.shadowBlur = 0;

      for (var spark = 0; spark < (W < 700 ? 5 : 8); spark++) {
        lane = (spark * 3 + s) % lanes; row = (spark * 7 + s * 2) % rows;
        var sn = nodes[lane][row], pulse = 0.45 + 0.45 * Math.sin(t * (0.8 + spark * 0.03) + spark + s);
        if (pulse <= 0.1) continue;
        var sr = Math.max(1.2, W * 0.0018) * pulse;
        var sg = ctx.createRadialGradient(sn.x, sn.y, 0, sn.x, sn.y, sr * 4);
        sg.addColorStop(0, "rgba(235,250,255," + (0.64 * lvl) + ")");
        sg.addColorStop(0.25, "rgba(140,215,255," + (0.44 * lvl) + ")");
        sg.addColorStop(1, "rgba(70,150,255,0)");
        ctx.fillStyle = sg; ctx.beginPath(); ctx.arc(sn.x, sn.y, sr * 4, 0, 6.283); ctx.fill();
      }

      var blobs = W < 700 ? 2 : 3;
      for (var k = 0; k < blobs; k++) {
        var sk = s * 3 + k * 1.7;
        var bx = cx + (Math.sin(t * (0.22 + 0.07 * k) + sk) * 0.6 + Math.sin(t * (0.11 + 0.05 * k) + sk * 1.9) * 0.4) * w * 0.34 * (1 + P.jitter);
        var by = top + h * (0.5 + (Math.sin(t * (0.17 + 0.06 * k) + sk * 1.3) * 0.5 + Math.sin(t * (0.09 + 0.04 * k) + sk * 2.1) * 0.5) * 0.42);
        var br = w * (0.26 + 0.08 * Math.sin(t * 0.5 + sk));
        var pa = Math.max(0, 0.07 + 0.05 * Math.sin(t * 0.85 + sk)) * lvl;
        var g = ctx.createRadialGradient(bx, by, 0, bx, by, br);
        g.addColorStop(0, "rgba(150,205,255," + pa + ")");
        g.addColorStop(0.6, "rgba(60,140,255," + (pa * 0.4) + ")");
        g.addColorStop(1, "rgba(50,120,255,0)");
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(bx, by, br, 0, 6.283); ctx.fill();
      }
      ctx.restore();
    }

    function pedestalRing(cxp, cfg, lvl) {
      var cx = cxp / 100 * W, ry0 = cfg.ringY * H;
      var rx = cfg.ringRX * W, ry = cfg.ringRY * H;
      ctx.save();
      ctx.shadowColor = "rgba(75,170,255,.9)"; ctx.shadowBlur = Math.max(4, W * 0.008);
      ctx.strokeStyle = "rgba(165,220,255," + (0.78 * lvl) + ")";
      ctx.lineWidth = Math.max(1.3, W * 0.0015);
      ctx.beginPath(); ctx.ellipse(cx, ry0, rx, ry, 0, 0, Math.PI); ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.strokeStyle = "rgba(95,180,255," + (0.28 * lvl) + ")";
      ctx.lineWidth = Math.max(2.6, W * 0.0036);
      ctx.beginPath(); ctx.ellipse(cx, ry0, rx, ry, 0, 0, Math.PI); ctx.stroke();
      ctx.restore();
    }

    function vesselFlame(bx, by, sc, seed, inten) {
      var sway = Math.sin(t * (2.2 + seed * 0.03) + seed * 1.7) * 2.2 * sc;
      var h = (24 + 7 * Math.sin(t * (2.8 + seed * 0.02) + seed)) * sc;
      var w = 8.5 * sc;
      ctx.save();
      ctx.shadowColor = "rgba(255,105,22,.85)"; ctx.shadowBlur = Math.max(2, 8 * sc);
      var outer = ctx.createLinearGradient(bx, by, bx, by - h);
      outer.addColorStop(0, "rgba(255,92,18,.92)");
      outer.addColorStop(0.48, "rgba(255,153,32,.94)");
      outer.addColorStop(1, "rgba(255,213,92,.12)");
      ctx.fillStyle = outer;
      ctx.beginPath(); ctx.moveTo(bx - w, by);
      ctx.bezierCurveTo(bx - w * 0.95, by - h * 0.30, bx - w * 0.18 + sway, by - h * 0.66, bx + sway, by - h);
      ctx.bezierCurveTo(bx + w * 0.20 + sway, by - h * 0.62, bx + w, by - h * 0.32, bx + w, by);
      ctx.quadraticCurveTo(bx, by - h * 0.12, bx - w, by); ctx.fill();
      ctx.shadowBlur = 0;
      var iH = h * (0.53 + 0.07 * inten), iW = w * 0.43;
      var inner = ctx.createLinearGradient(bx, by, bx, by - iH);
      inner.addColorStop(0, "rgba(255,244,170,.96)"); inner.addColorStop(1, "rgba(255,170,48,.10)");
      ctx.fillStyle = inner; ctx.beginPath(); ctx.moveTo(bx - iW, by);
      ctx.quadraticCurveTo(bx - iW * 0.4, by - iH * 0.55, bx + sway * 0.35, by - iH);
      ctx.quadraticCurveTo(bx + iW * 0.65, by - iH * 0.45, bx + iW, by);
      ctx.quadraticCurveTo(bx, by - iH * 0.10, bx - iW, by); ctx.fill();
      ctx.restore();
    }

    function fireEmitters(cfg, budget, perN, perI) {
      for (var f = 0; f < cfg.fires.length; f++) {
        var fe = cfg.fires[f], bx = fe.x / 100 * W, by = fe.y / 100 * H, sc = fe.s;
        var inten = 0.6 + 0.4 * fnoise(t * (0.35 + 0.05 * f), f * 3.7);
        var lr = (34 + 24 * inten) * sc, lg = ctx.createRadialGradient(bx, by - 7 * sc, 0, bx, by - 7 * sc, lr);
        lg.addColorStop(0, "rgba(255,168,76," + (0.24 * inten) + ")");
        lg.addColorStop(0.5, "rgba(255,92,24," + (0.11 * inten) + ")");
        lg.addColorStop(1, "rgba(120,40,10,0)");
        ctx.fillStyle = lg; ctx.beginPath(); ctx.arc(bx, by - 7 * sc, lr, 0, 6.283); ctx.fill();
        vesselFlame(bx, by, sc, f, inten);
        if (parts.length >= budget) continue;
        var n = perN + 1 + (inten * perI | 0);
        if (sc < 0.5 && ((fc + f) % 3)) n = 0;
        for (var i = 0; i < n; i++) parts.push({ x: bx + rnd(-2, 2) * sc, y: by, a: 0, mx: 22 + (Math.random() * 16 | 0), sw: rnd(-1, 1), ph: rnd(0, 6.28), sz: rnd(4, 8) * sc, sp: rnd(0.9, 1.4) * sc });
      }
    }

    function drawFire() {
      for (var i = parts.length - 1; i >= 0; i--) {
        var p = parts[i]; p.a++; var f = p.a / p.mx; if (f >= 1) { parts.splice(i, 1); continue; }
        var y = p.y - p.mx * p.sp * f, x = p.x + Math.sin(p.ph + f * 3.4) * p.sw * 9 * f, sz = p.sz * (1 - f * 0.6);
        var col = f < 0.3 ? "rgba(255,224,135," : f < 0.62 ? "rgba(255,142,38," : "rgba(205,55,18,", a = (1 - f) * 0.88;
        var g = ctx.createRadialGradient(x, y, 0, x, y, sz); g.addColorStop(0, col + a + ")"); g.addColorStop(1, col + "0)");
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, sz, 0, 6.283); ctx.fill();
      }
    }

    function frame() {
      if (!run) return;
      raf = requestAnimationFrame(frame);
      var mob = W < 700; fc++;
      if (mob && (fc & 1)) return;
      t += mob ? 0.032 : 0.016;

      var scene = root.getAttribute("data-scene");
      var baked = root.getAttribute("data-baked") === "1";
      var active = root.classList.contains("is-active") && !root.classList.contains("is-paused");
      ctx.clearRect(0, 0, W, H);

      /* Nothing to draw for the hall (its energy is in the artwork), and nothing to draw for a
         pedestal render that already ships with its own plasma and fire. */
      if (!active || scene !== "guardian" || baked) { if (parts.length) parts.length = 0; return; }

      curE += (1.08 - curE) * (mob ? 0.030 : 0.016);

      ctx.globalCompositeOperation = "lighter";
      plasmaColumn(GUARD.cols[0], GUARD, person(root.getAttribute("data-guardian")), curE);
      pedestalRing(GUARD.cols[0], GUARD, curE);
      fireEmitters(GUARD, mob ? 220 : 620, mob ? 0 : 1, 1);
      drawFire();
      ctx.globalCompositeOperation = "source-over";
    }

    function start() { if (run) return; run = true; resize(); curE = 1.0; raf = requestAnimationFrame(frame); }
    function still() { resize(); ctx.clearRect(0, 0, W, H); }

    if ("ResizeObserver" in window) {
      var ro = new ResizeObserver(resize);
      ro.observe(root); ro.observe(canvas);
    }
    window.addEventListener("resize", resize);
    return { start: start, still: still };
  }

  function init() {
    Array.prototype.slice.call(document.querySelectorAll('[data-room="guardian-chamber"]')).forEach(function (root) {
      var c = root.querySelector("[data-fx]"); if (!c) return;
      var e = Engine(root, c);
      if (reduced()) { e.still(); return; }
      e.start();
    });
  }
  if (document.readyState !== "loading") init(); else document.addEventListener("DOMContentLoaded", init);
})();
